import { Close, PinDrop } from '@mui/icons-material';
import {
    Box,
    Card,
    CardActionArea,
    Chip,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React from 'react';
import { CustomSmallListChip } from '../../../../../layout/styledcomponents/CustomChip';
import { camelCaseToTitleCase } from '../../../../../shared/utils';
import { Contractor, ModuleContractor } from '../../../../types/contractor';
import {
    optionLeadingIcon,
    serviceTypeLeadingIcon,
} from '../../../Contractors/contractorServiceIcons';

interface ContractorSelectionCardProps {
    contractor: Contractor;
    mode: 'picker' | 'selected';
    selected?: boolean;
    onToggle?: () => void;
    onRemove?: () => void;
}

const ContractorSelectionCard = ({
    contractor,
    mode,
    selected = false,
    onToggle,
    onRemove,
}: ContractorSelectionCardProps) => {
    const serviceType = contractor.services.type;
    const serviceItems = contractor.services.items;
    const PlaceholderIcon = serviceTypeLeadingIcon(serviceType);

    const content = (
        <Stack
            direction='row'
            spacing={2}
            alignItems='flex-start'
            sx={{ width: '100%' }}
        >
            <Box
                sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    bgcolor: (t) =>
                        t.palette.mode === 'dark'
                            ? 'action.selected'
                            : alpha(t.palette.primary.main, 0.12),
                    color: 'primary.main',
                }}
            >
                <PlaceholderIcon sx={{ fontSize: 22 }} />
            </Box>

            <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 0.75,
                    }}
                >
                    <Typography fontWeight='bold' noWrap>
                        {contractor.name}
                    </Typography>
                    {serviceType && (
                        <CustomSmallListChip
                            size='small'
                            color='secondary'
                            label={camelCaseToTitleCase(serviceType)}
                            sx={{
                                bgcolor: (theme: any) =>
                                    alpha(theme.palette.secondary.main, 0.2),
                            }}
                        />
                    )}
                </Box>

                {serviceItems.length > 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.5,
                        }}
                    >
                        {serviceItems.map((item) => {
                            const ServiceIcon = optionLeadingIcon(item);

                            return (
                                <Chip
                                    key={item}
                                    size='small'
                                    icon={<ServiceIcon sx={{ fontSize: 18 }} />}
                                    label={camelCaseToTitleCase(item)}
                                />
                            );
                        })}
                    </Box>
                )}

                <Stack direction='row' spacing={0.5} alignItems='center'>
                    <PinDrop sx={{ fontSize: 16 }} color='action' />
                    <Typography variant='body2' color='text.secondary'>
                        {contractor.serviceStates
                            .map((state) => state.state)
                            .join(', ')}
                    </Typography>
                </Stack>

                {contractor.hasPricingTemplates === false && (
                    <Typography variant='caption' color='warning.main'>
                        No pricing templates
                    </Typography>
                )}
            </Stack>

            {mode === 'selected' && onRemove && (
                <IconButton
                    aria-label={`Remove ${contractor.name}`}
                    size='small'
                    onClick={(event) => {
                        event.stopPropagation();
                        onRemove();
                    }}
                    sx={{ flexShrink: 0 }}
                >
                    <Close fontSize='small' />
                </IconButton>
            )}
        </Stack>
    );

    if (mode === 'picker') {
        return (
            <Card
                variant='outlined'
                sx={{
                    borderRadius: 2,
                    borderColor: selected ? 'primary.main' : 'divider',
                    bgcolor: selected ? 'action.selected' : 'background.paper',
                }}
            >
                <CardActionArea
                    onClick={onToggle}
                    aria-label={contractor.name}
                    aria-pressed={selected}
                    sx={{ p: 2, alignItems: 'stretch' }}
                >
                    {content}
                </CardActionArea>
            </Card>
        );
    }

    return (
        <Card variant='outlined' sx={{ borderRadius: 2, p: 2 }}>
            {content}
        </Card>
    );
};

export default ContractorSelectionCard;
